import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Building2, Package, LogOut, Plus, Calendar, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';

export default function VendorDashboard() {
  const [properties, setProperties] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [showPropertyDialog, setShowPropertyDialog] = useState(false);
  const [showGiftDialog, setShowGiftDialog] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchVendorData();
  }, []);

  const fetchVendorData = async () => {
    try {
      const [propertiesRes, bookingsRes, statsRes] = await Promise.all([
        api.getVendorProperties(),
        api.getBookings(),
        api.getVendorStats(),
      ]);
      setProperties(propertiesRes.data);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch vendor data:', error);
    }
  };

  const handleCreateProperty = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const amenities = formData.get('amenities').split(',').map(a => a.trim());
    const images = formData.get('images').split(',').map(i => i.trim());

    try {
      await api.createProperty({
        name: formData.get('name'),
        location: formData.get('location'),
        city: formData.get('city'),
        capacity: parseInt(formData.get('capacity')),
        price_per_day: parseFloat(formData.get('price_per_day')),
        amenities,
        images,
        description: formData.get('description'),
        property_type: formData.get('property_type'),
        featured: false,
      });
      toast.success('Property created successfully!');
      setShowPropertyDialog(false);
      fetchVendorData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create property');
    }
  };

  const handleCreateGift = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const images = formData.get('images').split(',').map(i => i.trim());

    try {
      await api.createGift({
        name: formData.get('name'),
        category: formData.get('category'),
        price: parseFloat(formData.get('price')),
        images,
        description: formData.get('description'),
        stock: parseInt(formData.get('stock')),
      });
      toast.success('Gift created successfully!');
      setShowGiftDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create gift');
    }
  };

  const handleUpdateBookingStatus = async (bookingId, newStatus) => {
    try {
      await api.updateBookingStatus(bookingId, newStatus);
      toast.success('Booking status updated');
      fetchVendorData();
    } catch (error) {
      toast.error('Failed to update booking status');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png" 
              alt="My Corporate Hub" 
              className="h-12 w-auto"
            />
            <h1 className="luxury-text text-xl font-semibold text-midnight-navy">Vendor Dashboard</h1>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/vendor/rfps">
              <Button className="bg-navy hover:bg-navy-700 text-white font-semibold shadow-md">
                <FileText className="w-4 h-4 mr-2" /> RFP Bidding
              </Button>
            </Link>
            <Link to="/vendor/open-rfps">
              <Button className="bg-gold hover:bg-gold-light text-navy-900 font-semibold shadow-md">
                <FileText className="w-4 h-4 mr-2" /> Bulk RFPs
              </Button>
            </Link>
            <Link to="/vendor/inventory">
              <Button className="bg-navy hover:bg-navy-700 text-white font-semibold shadow-md">
                <Calendar className="w-4 h-4 mr-2" /> Inventory
              </Button>
            </Link>
            <Link to="/vendor/api">
              <Button className="bg-navy hover:bg-navy-700 text-white font-semibold shadow-md">
                API Keys
              </Button>
            </Link>
            <span className="text-sm text-slate-800">Welcome, {user?.name}</span>
            <Button onClick={() => { logout(); navigate('/'); }} variant="outline" size="sm">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white p-6 rounded-sm border border-slate-200">
              <Building2 className="w-8 h-8 text-champagne-gold mb-4" />
              <p className="text-3xl luxury-text font-semibold mb-2 text-slate-900">{stats.total_properties}</p>
              <p className="text-sm text-slate-800">Total Properties</p>
            </div>
            <div className="bg-white p-6 rounded-sm border border-slate-200">
              <Package className="w-8 h-8 text-champagne-gold mb-4" />
              <p className="text-3xl luxury-text font-semibold mb-2 text-slate-900">{stats.total_bookings}</p>
              <p className="text-sm text-slate-800">Total Bookings</p>
            </div>
            <div className="bg-white p-6 rounded-sm border border-slate-200">
              <Package className="w-8 h-8 text-champagne-gold mb-4" />
              <p className="text-3xl luxury-text font-semibold mb-2 text-slate-900">{stats.total_gifts}</p>
              <p className="text-sm text-slate-800">Total Gifts</p>
            </div>
          </div>
        )}

        <Tabs defaultValue="properties">
          <TabsList>
            <TabsTrigger value="properties">My Properties</TabsTrigger>
            <TabsTrigger value="bookings">Bookings</TabsTrigger>
            <TabsTrigger value="api" onClick={() => navigate('/vendor/api')}>API Integration</TabsTrigger>
          </TabsList>

          <TabsContent value="properties" className="mt-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="luxury-text text-2xl font-medium text-slate-900">Properties</h2>
              <Button onClick={() => setShowPropertyDialog(true)} className="bg-midnight-navy">
                <Plus className="w-4 h-4 mr-2" />
                Add Property
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {properties.map((property) => (
                <div key={property.id} className="bg-white p-6 rounded-sm border border-slate-200">
                  <h3 className="luxury-text text-xl font-medium text-slate-900 mb-2 text-slate-900">{property.name}</h3>
                  <p className="text-sm text-slate-800 mb-4">{property.city} • {property.capacity} guests</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl luxury-text font-semibold text-slate-900">₹{property.price_per_day.toLocaleString()}</span>
                    <span className="text-sm text-slate-800">{property.rating} ⭐ ({property.total_reviews})</span>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="bookings" className="mt-8">
            <div className="space-y-4">
              {bookings.map((booking) => (
                <div key={booking.id} className="bg-white p-6 rounded-sm border border-slate-200">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="luxury-text text-xl font-medium text-slate-900">{booking.property_name}</h3>
                      <p className="text-sm text-slate-800">Client: {booking.client_name}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-sm text-xs font-semibold ${
                      booking.status === 'confirmed' ? 'bg-emerald-100 text-emerald-800' : 
                      'bg-amber-100 text-amber-800'
                    }`}>
                      {booking.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex gap-4 text-sm text-slate-800 mb-4">
                    <span>Event: {booking.event_type}</span>
                    <span>Guests: {booking.guests}</span>
                    <span>Amount: ₹{booking.total_amount.toLocaleString()}</span>
                  </div>
                  {booking.status === 'pending' && booking.payment_status === 'paid' && (
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleUpdateBookingStatus(booking.id, 'confirmed')}>
                        Confirm
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleUpdateBookingStatus(booking.id, 'cancelled')}>
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={showPropertyDialog} onOpenChange={setShowPropertyDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add New Property</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateProperty} className="space-y-4">
            <div>
              <Label htmlFor="name">Property Name</Label>
              <Input id="name" name="name" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="location">Location</Label>
                <Input id="location" name="location" required />
              </div>
              <div>
                <Label htmlFor="city">City</Label>
                <Input id="city" name="city" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="capacity">Capacity</Label>
                <Input id="capacity" name="capacity" type="number" required />
              </div>
              <div>
                <Label htmlFor="price_per_day">Price per Day (₹)</Label>
                <Input id="price_per_day" name="price_per_day" type="number" step="0.01" required />
              </div>
            </div>
            <div>
              <Label htmlFor="property_type">Property Type</Label>
              <Select name="property_type" defaultValue="hotel">
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hotel">Hotel</SelectItem>
                  <SelectItem value="resort">Resort</SelectItem>
                  <SelectItem value="convention_center">Convention Center</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="amenities">Amenities (comma-separated)</Label>
              <Input id="amenities" name="amenities" placeholder="WiFi, Parking, Pool" required />
            </div>
            <div>
              <Label htmlFor="images">Image URLs (comma-separated)</Label>
              <Textarea id="images" name="images" placeholder="https://..." required />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea id="description" name="description" required />
            </div>
            <Button type="submit" className="w-full bg-midnight-navy">Create Property</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showGiftDialog} onOpenChange={setShowGiftDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Gift</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateGift} className="space-y-4">
            <div>
              <Label htmlFor="gift-name">Gift Name</Label>
              <Input id="gift-name" name="name" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Category</Label>
                <Input id="category" name="category" required />
              </div>
              <div>
                <Label htmlFor="gift-price">Price (₹)</Label>
                <Input id="gift-price" name="price" type="number" step="0.01" required />
              </div>
            </div>
            <div>
              <Label htmlFor="stock">Stock Quantity</Label>
              <Input id="stock" name="stock" type="number" required />
            </div>
            <div>
              <Label htmlFor="gift-images">Image URLs (comma-separated)</Label>
              <Textarea id="gift-images" name="images" required />
            </div>
            <div>
              <Label htmlFor="gift-description">Description</Label>
              <Textarea id="gift-description" name="description" required />
            </div>
            <Button type="submit" className="w-full bg-midnight-navy">Create Gift</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
